import { axiosInstance } from '../../utils/axios'
import type {
  SystemHealthSummary,
  SystemHealthActivity,
  SystemHealthStats,
  SystemHealthConfig,
  SystemHealthRecipient,
  SystemHealthRecipientInput,
  SystemHealthReportResult,
  ServiceBusHealth,
  SystemHealthProcessorVersion,
  SystemHealthFailed,
} from '../../types/systemHealth'

interface ApiResponse<T> {
  success: boolean
  data: T
}

const BASE_URL = '/admin/system-health'

export const systemHealthApi = {
  summary: async (): Promise<SystemHealthSummary> => {
    const res = await axiosInstance.get<ApiResponse<SystemHealthSummary>>(`${BASE_URL}/summary`)
    return res.data.data
  },
  activity: async (): Promise<SystemHealthActivity> => {
    const res = await axiosInstance.get<ApiResponse<SystemHealthActivity>>(`${BASE_URL}/activity`)
    return res.data.data
  },
  stats: async (): Promise<SystemHealthStats> => {
    const res = await axiosInstance.get<ApiResponse<SystemHealthStats>>(`${BASE_URL}/stats`)
    return res.data.data
  },
  serviceBus: async (): Promise<ServiceBusHealth> => {
    const res = await axiosInstance.get<ApiResponse<ServiceBusHealth>>(`${BASE_URL}/service-bus`)
    return res.data.data
  },
  failed: async (days: number): Promise<SystemHealthFailed> => {
    const res = await axiosInstance.get<ApiResponse<SystemHealthFailed>>(`${BASE_URL}/failed`, {
      params: { days },
    })
    return res.data.data
  },
  processorVersion: async (): Promise<SystemHealthProcessorVersion> => {
    const res = await axiosInstance.get<ApiResponse<SystemHealthProcessorVersion>>(`${BASE_URL}/version`)
    return res.data.data
  },

  // --- Alert config (singleton) ---
  getConfig: async (): Promise<SystemHealthConfig> => {
    const res = await axiosInstance.get<ApiResponse<SystemHealthConfig>>(`${BASE_URL}/config`)
    return res.data.data
  },
  updateConfig: async (input: Partial<SystemHealthConfig>): Promise<SystemHealthConfig> => {
    const res = await axiosInstance.patch<ApiResponse<SystemHealthConfig>>(`${BASE_URL}/config`, input)
    return res.data.data
  },

  // --- Alert recipients (ops list) ---
  listRecipients: async (): Promise<SystemHealthRecipient[]> => {
    const res = await axiosInstance.get<ApiResponse<SystemHealthRecipient[]>>(`${BASE_URL}/recipients`)
    return res.data.data
  },
  createRecipient: async (input: SystemHealthRecipientInput): Promise<SystemHealthRecipient> => {
    const res = await axiosInstance.post<ApiResponse<SystemHealthRecipient>>(`${BASE_URL}/recipients`, input)
    return res.data.data
  },
  updateRecipient: async (id: number, input: Partial<SystemHealthRecipientInput>): Promise<SystemHealthRecipient> => {
    const res = await axiosInstance.patch<ApiResponse<SystemHealthRecipient>>(`${BASE_URL}/recipients/${id}`, input)
    return res.data.data
  },
  deleteRecipient: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/recipients/${id}`)
  },

  // --- Send a status report to all enabled recipients (via pipeline) ---
  sendReport: async (): Promise<SystemHealthReportResult> => {
    const res = await axiosInstance.post<ApiResponse<SystemHealthReportResult>>(`${BASE_URL}/send-report`)
    return res.data.data
  },
}
